import React from 'react'
import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'
import type { Modifier } from '@popperjs/core'
import { HorizontalStack } from '../HorizontalStack'
import { TooltipPopOver } from '../TooltipPopOver'
import styles from './index.module.scss'

const ENTITY_PREVIEW_WIDTH = 320

// `TooltipPopOver`'s popper computes its position once, when the popover first shows — before
// `EntityPreview`'s query resolves and the preview grows into its final size (a list section's
// item rows only exist once loaded). A plain `ResizeObserver` on the popper element itself,
// scoped to THIS modifier instance only, catches that growth and calls `instance.update()` — the
// shared `TooltipPopOver` default stays untouched, and nothing outside this one popper instance
// is affected (no global `resize` dispatch, which would also re-run every OTHER resize listener
// in the app — grid layouts, charts, react-select placement — just to move one popover).
const updateOnResizeModifier: Modifier<'updateOnResize', Record<string, never>> = {
  name: 'updateOnResize',
  enabled: true,
  phase: 'write',
  fn: () => undefined,
  effect: ({ state, instance }) => {
    const observer = new ResizeObserver(() => instance.update())
    observer.observe(state.elements.popper)
    return () => observer.disconnect()
  },
}

export type EntityCodeLinkProps = {
  code: string
  to: string
  preview: ReactNode
  statusDot?: ReactNode
  target?: string
}

/** A related-record reference: a status dot and a bold code link, with a hover preview — no card;
 * every domain `*CodeLink` (`OrderCodeLink.tsx` etc.) supplies the code, dot and preview body on
 * this shared shape. `text-nowrap`: a code must never break at its own hyphen. `stopPropagation`:
 * react-router's `Link` never stops the click, so without it a code click also reaches
 * `InlineInput`'s click-to-edit wrapper. `popperConfig`: `altAxis` lets Popper shift the preview
 * vertically to stay inside the viewport when the code sits near the bottom edge; `updateOnResize`
 * (above) keeps that repositioning correct once the preview's data loads in and it grows.
 * `styles.link`: dark mode only, see `index.module.scss`'s comment (a hovered/striped row's
 * tinted background drops the default link colour's contrast under 4.5:1). */
export const EntityCodeLink = ({ code, to, preview, statusDot, target }: EntityCodeLinkProps) => (
  <TooltipPopOver
    tooltipBody={preview}
    width={ENTITY_PREVIEW_WIDTH}
    popperConfig={{ modifiers: [{ name: 'preventOverflow', options: { altAxis: true } }, updateOnResizeModifier] }}
  >
    <HorizontalStack gap={1}>
      {statusDot}
      <Link
        to={to}
        target={target}
        rel="noopener noreferrer"
        className={`fw-bold text-nowrap ${styles.link}`}
        onClick={(event) => event.stopPropagation()}
      >
        {code}
      </Link>
    </HorizontalStack>
  </TooltipPopOver>
)
